import {
    Clock3hIcon as ClockIcon,
    EnvelopeIcon as MailIcon,
    PinIcon as MapPin,
    PhoneIcon,
} from "@wags/ui/product-icons.tsx";

import { cn } from "@wags/ui/ui-utils";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { ClickableStaticMap } from "@/components/shared/ClickableStaticMap";
import type { ProviderLocationInput } from "../provider-locations";
import { OtherLocations } from "../provider-locations-column";

function InfoRow({
    icon: Icon,
    children,
}: {
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    children: ReactNode;
}) {
    return (
        <div className="flex items-center gap-2.5">
            <Icon className="size-4 shrink-0 text-doberman-black/60" />
            <p className="min-w-0 break-words text-body text-doberman-black/80">
                {children}
            </p>
        </div>
    );
}

export function WhatToExpectSection({
    locationDisplay,
    staticMapSrc,
    isApptOnly,
    phone,
    email,
    coordinates,
    locations,
}: {
    locationDisplay: string;
    staticMapSrc?: string | null;

    isApptOnly?: boolean | null;
    phone?: string | null;
    email?: string | null;
    coordinates?: { lat: number; lng: number } | null;
    locations: readonly ProviderLocationInput[];
}) {
    const hasVisitColumn =
        isApptOnly != null || Boolean(phone) || Boolean(email);
    return (
        <section className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-x-16">
            <h2 className="text-section-title text-doberman-black text-balance">
                What to expect
            </h2>
            <div
                className={cn(
                    "mt-5 grid gap-8 md:items-start lg:mt-0 lg:max-w-190",
                    hasVisitColumn && "md:grid-cols-2",
                )}
            >
                <div>
                    <h3 className="text-body-small font-medium text-doberman-black/55">
                        Location
                    </h3>
                    {staticMapSrc && coordinates ? (
                        <ClickableStaticMap
                            staticMapSrc={staticMapSrc}
                            alt={
                                locationDisplay
                                    ? `Map showing ${locationDisplay} location`
                                    : "Map showing the shelter location"
                            }
                            coordinates={coordinates}
                            className="mt-3 aspect-2/1 w-full rounded-card"
                        />
                    ) : (
                        <div className="mt-3 flex aspect-2/1 w-full items-center justify-center rounded-card bg-doberman-black/5">
                            <MapPin className="size-6 text-doberman-black/20" />
                        </div>
                    )}
                    {locationDisplay ? (
                        <p className="mt-3 text-label text-doberman-black">
                            Visit in {locationDisplay}
                        </p>
                    ) : null}
                    <OtherLocations
                        locations={locations}
                        shownLabel={locationDisplay}
                    />
                </div>
                {hasVisitColumn && (
                    <div>
                        <h3 className="text-body-small font-medium text-doberman-black/55">
                            Visit info
                        </h3>
                        <div className="mt-3 flex flex-col gap-3">
                            {isApptOnly != null && (
                                <InfoRow icon={ClockIcon}>
                                    {isApptOnly
                                        ? "By appointment only"
                                        : "Walk-ins welcome"}
                                </InfoRow>
                            )}
                            {phone && (
                                <InfoRow icon={PhoneIcon}>{phone}</InfoRow>
                            )}
                            {email && (
                                <InfoRow icon={MailIcon}>{email}</InfoRow>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
